import React, { useCallback, useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
// @ts-ignore - Webpack requires the '/menus' alias defined in package.json exports
import { BubbleMenu, FloatingMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import ImageResize from 'tiptap-extension-resize-image';
import Underline from '@tiptap/extension-underline';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import Typography from '@tiptap/extension-typography';
import { Markdown } from 'tiptap-markdown';
import { CharacterCount } from '@tiptap/extension-character-count';
import { HorizontalRule } from '@tiptap/extension-horizontal-rule';
import { common, createLowlight } from 'lowlight';
import { AnimatePresence, motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import RichTextRenderer from '../../../common/RichTextRenderer';
import {
  BiBold, BiItalic, BiUnderline, BiStrikethrough,
  BiListUl, BiListOl, BiCode, BiCodeBlock,
  BiLink, BiImage, BiUndo, BiRedo,
  BiPaperclip, BiCheckSquare, BiHighlight,
  BiAlignLeft, BiAlignMiddle, BiAlignRight, BiAlignJustify,
  BiTable, BiMinus, BiFullscreen, BiExitFullscreen, BiPlus
} from 'react-icons/bi';
import {
  TbSubscript, TbSuperscript, TbBlockquote, TbClearFormatting,
  TbH1, TbH2, TbH3, TbPalette, TbMarkdown, TbEye
} from 'react-icons/tb';
import toast from 'react-hot-toast';
import axios from '../../../../utils/api/axios';
import './styles.css';

const lowlight = createLowlight(common);

export interface MentionItem {
  type: 'report' | 'retest' | 'pentest_report' | 'user' | 'admin_team';
  id: number | string;
  title: string;
  subtitle?: string;
  pfp?: string | null;
}

export interface RichTextEditorRef {
  getEditor: () => Editor | null;
  insertContent: (content: string) => void;
  insertMention: (mentionText: string) => void;
  focus: () => void;
  clear: () => void;
  getHTML: () => string;
  getText: () => string;
}

export interface RichTextEditorProps {
  value?: string;
  onChange?: (html: string, text?: string) => void;
  onSubmit?: () => void;
  placeholder?: string;
  disabled?: boolean;
  minHeight?: string;
  maxHeight?: string;
  onTextChange?: (text: string, cursorPos: number) => void;
  showToolbar?: boolean;
  compact?: boolean;
  className?: string;
  onCtrlEnter?: () => void;
  outputFormat?: 'html' | 'markdown';
}

// Toolbar button component
const ToolbarButton: React.FC<{
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
  label?: string;
}> = ({ onClick, active, disabled, title, children, label }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`p-1.5 rounded-md transition-all duration-150 flex items-center gap-1 ${
      active 
        ? 'bg-blue-100 text-blue-600' 
        : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    {children}
    {label && <span className="text-xs font-semibold uppercase tracking-wider px-1">{label}</span>}
  </button>
);

const ModeTab: React.FC<{
  active: boolean;
  onClick: () => void;
  label: string;
  icon: React.ReactNode;
}> = ({ active, onClick, label, icon }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex items-center gap-2 px-5 py-2.5 text-xs font-bold uppercase tracking-widest transition-all duration-200 border-b-2 ${
      active 
        ? 'border-blue-600 text-blue-700 bg-blue-50/40' 
        : 'border-transparent text-gray-400 hover:text-gray-600 hover:bg-gray-50/50'
    }`}
  >
    {icon}
    {label}
  </button>
);

// Toolbar divider
const ToolbarDivider = () => (
  <div className="w-px h-5 bg-gray-200 mx-1" />
);

const RichTextEditor = forwardRef<RichTextEditorRef, RichTextEditorProps>(({
  value = '',
  onChange,
  onSubmit,
  placeholder = 'Type your message...',
  disabled = false,
  minHeight = '300px',
  maxHeight = '800px',
  onTextChange,
  showToolbar = true,
  compact = false,
  className = '',
  onCtrlEnter,
  outputFormat = 'html'
}, ref) => {
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showHighlightPicker, setShowHighlightPicker] = useState(false);
  const [showTableMenu, setShowTableMenu] = useState(false);
  const [isMarkdownMode, setIsMarkdownMode] = useState(false);
  const [markdownValue, setMarkdownValue] = useState('');
  const [showSplitView, setShowSplitView] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const isMarkdownModeRef = useRef(isMarkdownMode);
  const onChangeRef = useRef(onChange);
  const outputFormatRef = useRef(outputFormat);
  const lastSentValueRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    isMarkdownModeRef.current = isMarkdownMode;
  }, [isMarkdownMode]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    outputFormatRef.current = outputFormat;
  }, [outputFormat]);

  // Color palette for text and highlights
  const textColors = [
    '#000000', '#374151', '#DC2626', '#EA580C', '#D97706', '#CA8A04',
    '#65A30D', '#16A34A', '#0891B2', '#2563EB', '#7C3AED', '#C026D3'
  ];
  const highlightColors = [
    '#FEF08A', '#FDE68A', '#FED7AA', '#FECACA', '#E9D5FF', '#BFDBFE',
    '#A7F3D0', '#D1FAE5', '#CCFBF1', '#E0E7FF', '#FCE7F3', '#F3E8FF'
  ];

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: false, // We use CodeBlockLowlight instead
        horizontalRule: false, // We use our own
      }),
      Placeholder.configure({
        placeholder,
        emptyEditorClass: 'is-editor-empty',
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 hover:underline cursor-pointer',
        },
      }),
      ImageResize.configure({
        inline: true,
        allowBase64: true,
        HTMLAttributes: {
          class: 'max-w-full rounded-lg my-2 resize-image',
        },
      }),
      Underline,
      TaskList.configure({
        HTMLAttributes: {
          class: 'not-prose pl-2',
        },
      }),
      TaskItem.configure({
        nested: true,
        HTMLAttributes: {
          class: 'flex items-start gap-2',
        },
      }),
      CodeBlockLowlight.configure({
        lowlight,
        HTMLAttributes: {
          class: 'bg-gray-900 text-gray-100 rounded-lg p-3 my-2 text-sm font-mono overflow-x-auto',
        },
      }),
      // Text alignment
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      // Highlight / marker
      Highlight.configure({
        multicolor: true,
        HTMLAttributes: {
          class: 'bg-yellow-200 px-0.5 rounded',
        },
      }),
      // Text color support
      TextStyle,
      Color,
      // Subscript & Superscript
      Subscript,
      Superscript,
      // Table support
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'border-collapse table-auto w-full my-2',
        },
      }),
      TableRow,
      TableCell.configure({
        HTMLAttributes: {
          class: 'border border-gray-300 p-2',
        },
      }),
      TableHeader.configure({
        HTMLAttributes: {
          class: 'border border-gray-300 p-2 bg-gray-100 font-semibold',
        },
      }),
      // Character count
      CharacterCount.configure({
        limit: 100000,
      }),
      // Typography (smart quotes, etc)
      Typography,
      // Markdown support
      Markdown.configure({
        html: true,
        tightLists: true, // Standardizes list output to prevent numbering resets
        bulletListMarker: '-',
        linkify: true,
        breaks: true, // Re-enable to handle standard line breaks correctly
      }),
      HorizontalRule.configure({
        HTMLAttributes: {
          class: 'my-8 border-t border-gray-200',
        },
      }),
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor: updatedEditor }: { editor: Editor }) => {
      const html = updatedEditor.getHTML();
      const text = updatedEditor.getText();
      const md = (updatedEditor.storage as any).markdown.getMarkdown();
      
      if (!isMarkdownModeRef.current) {
        setMarkdownValue(md);
      }
      
      const newValue = outputFormatRef.current === 'markdown' ? md : html;
      lastSentValueRef.current = newValue;
      
      if (outputFormatRef.current === 'markdown') {
        onChangeRef.current?.(md, html);
      } else {
        onChangeRef.current?.(html, md);
      }
    },
    editorProps: {
      attributes: {
        class: `prose prose-sm max-w-none focus:outline-none px-3 py-2 ${compact ? 'min-h-[40px]' : ''}`,
        style: `min-height: ${minHeight}; max-height: ${maxHeight}; overflow-y: auto;`,
      },
      // Clean up HTML pasted from external sources like ChatGPT
      transformPastedHTML(html) {
        return html
          .replace(/<pre[^>]*><code[^>]*><\/code><\/pre>/g, '') // Remove empty code blocks
          .replace(/<pre[^>]*>\s*<\/pre>/g, '') // Remove empty pre tags
          .replace(/<li>\s*<p><\/p>/g, '<li>') // Remove empty leading paragraphs in list items
          .replace(/<p>&nbsp;<\/p>/g, '<br>') // Convert empty space paragraphs to simple breaks
          .replace(/(<br\s*\/?>){3,}/g, '<br><br>'); // Max 2 consecutive breaks
      },
      handleKeyDown: (_view: any, event: KeyboardEvent) => {
        // Submit on Ctrl/Cmd + Enter
        if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
          event.preventDefault();
          onSubmit?.();
          onCtrlEnter?.();
          return true;
        }
        return false;
      },
    },
  });

  // Sync external value changes
  useEffect(() => {
    if (!editor) return;

    if (isMarkdownMode) {
      if (value !== lastSentValueRef.current) {
        editor.commands.setContent(value);
        const md = (editor.storage as any).markdown.getMarkdown();
        setMarkdownValue(md);
        lastSentValueRef.current = value;
      }
      return;
    }

    // If the editor is focused and the change matches what we last sent (ignoring whitespace and tags),
    // skip updating to prevent cursor jumping, loss of spaces, or history reset.
    if (editor.isFocused && lastSentValueRef.current !== undefined) {
      const cleanString = (str: string) => str.replace(/<[^>]*>/g, '').replace(/\s+/g, '').trim();
      if (cleanString(value) === cleanString(lastSentValueRef.current)) {
        return;
      }
    }

    // Normalize value to parsed HTML for robust comparison with editor.getHTML()
    let normalizedValueHTML = '';
    if (value === '') {
      normalizedValueHTML = '<p></p>';
    } else if (value.includes('<') && value.includes('>')) {
      // Looks like HTML
      normalizedValueHTML = value;
    } else {
      // Assume Markdown: parse to HTML using the editor's markdown parser
      normalizedValueHTML = (editor.storage as any).markdown?.parser?.parse(value) || value;
    }

    const normalize = (html: string) => {
      return html
        .replace(/\s+/g, ' ')
        .replace(/\/?>/g, '>')
        .trim();
    };

    const currentHTML = editor.getHTML();
    if (normalize(normalizedValueHTML) !== normalize(currentHTML)) {
      if (value === '') {
        editor.commands.setContent('');
      } else {
        // tiptap-markdown's overridden setContent correctly handles both HTML and Markdown strings
        editor.commands.setContent(value);
      }
    }
  }, [value, editor, isMarkdownMode]);

  // Expose editor methods via ref
  useImperativeHandle(ref, () => ({
    getEditor: () => editor,
    insertContent: (content: string) => {
      editor?.chain().focus().insertContent(content).run();
    },
    insertMention: (mentionText: string) => {
      // Insert mention as a styled span
      editor?.chain().focus().insertContent(mentionText).run();
    },
    focus: () => {
      editor?.chain().focus().run();
    },
    clear: () => {
      editor?.commands.setContent('');
    },
    getHTML: () => editor?.getHTML() || '',
    getText: () => editor?.getText() || '',
  }), [editor]);

  // Notify parent of text changes for mention detection
  useEffect(() => {
    if (!editor || !onTextChange) return;

    const handleUpdate = () => {
      const text = editor.getText();
      // Get cursor position from the editor state
      const { from } = editor.state.selection;
      onTextChange(text, from);
    };

    editor.on('update', handleUpdate);
    editor.on('selectionUpdate', handleUpdate);

    return () => {
      editor.off('update', handleUpdate);
      editor.off('selectionUpdate', handleUpdate);
    };
  }, [editor, onTextChange]);

  // File upload handler
  const handleFileUpload = useCallback(async (files: FileList | null, insertAsImage = false) => {
    if (!files || files.length === 0) return;

    const BLOCKED_EXTENSIONS = [
      "exe", "dll", "so", "dylib", "bat", "cmd", "sh", "ps1", "vbs", "js", "ts", "html", "htm", "php",
      "jsp", "asp", "aspx", "cgi", "pl", "py", "rb", "jar", "msi", "scr", "pif", "com", "lnk", "wsf", "svg"
    ];
    
    const ALLOWED_EXTENSIONS = [
      "png", "jpg", "jpeg", "gif", "webp", "bmp", "ico",
      "mp4", "mov", "webm", "avi", "mkv", "3gp",
      "mp3", "wav", "ogg", "aac", "flac", "m4a",
      "pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt", "csv", "rtf", "odt", "ods", "odp",
      "zip", "rar", "tar", "gz", "7z", "json", "xml", "yaml", "yml", "pcap", "log"
    ];

    const invalidFile = Array.from(files).find(file => {
      const ext = file.name.toLowerCase().split('.').pop() || '';
      return BLOCKED_EXTENSIONS.includes(ext) || !ALLOWED_EXTENSIONS.includes(ext);
    });

    if (invalidFile) {
      toast.error(`File type not allowed: ${invalidFile.name}`);
      return;
    }

    setUploading(true);
    const formData = new FormData();

    Array.from(files).forEach(file => {
      formData.append('files', file);
    });

    try {
      const response = await axios.post('/v2/uploads/rte', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data?.files) {
        response.data.files.forEach((url: string) => {
          if (insertAsImage || /\.(jpg|jpeg|png|gif|webp)$/i.test(url)) {
            editor?.chain().focus().setImage({ src: url }).run();
          } else {
            // Insert as a link for non-image files
            const fileName = url.split('/').pop() || 'File';
            editor?.chain().focus().insertContent(`<a href="${url}" target="_blank" class="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-sm text-blue-600 hover:bg-gray-200">📎 ${fileName}</a> `).run();
          }
        });
        toast.success('File(s) uploaded successfully');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  }, [editor]);

  // Set link
  const setLink = useCallback(() => {
    if (!linkUrl) {
      editor?.chain().focus().unsetLink().run();
    } else {
      const url = linkUrl.startsWith('http') ? linkUrl : `https://${linkUrl}`;
      editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
    setShowLinkInput(false);
    setLinkUrl('');
  }, [editor, linkUrl]);

  // Intelligent Code Block Toggling
  const toggleIntelligentCodeBlock = useCallback(() => {
    if (!editor) return;

    // If we are already in a code block, toggle it off normally
    if (editor.isActive('codeBlock')) {
      editor.chain().focus().toggleCodeBlock().run();
      return;
    }

    const { from, to } = editor.state.selection;
    
    // Get the text content including newlines between blocks
    const text = editor.state.doc.textBetween(from, to, '\n');
    
    if (text.length > 0) {
      // Replace selection with a single code block node containing all captured text
      editor.chain()
        .focus()
        .insertContent({
          type: 'codeBlock',
          content: [{ type: 'text', text }]
        })
        .run();
    } else {
      // Just create an empty code block
      editor.chain().focus().toggleCodeBlock().run();
    }
  }, [editor]);

  // Handle paste for images
  useEffect(() => {
    if (!editor) return;

    const handlePaste = (event: ClipboardEvent) => {
      const items = event.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type.startsWith('image/')) {
          event.preventDefault();
          const file = item.getAsFile();
          if (file) {
            const dataTransfer = new DataTransfer();
            dataTransfer.items.add(file);
            handleFileUpload(dataTransfer.files, true);
          }
          break;
        }
      }
    };

    const editorDom = editor.view.dom;
    editorDom.addEventListener('paste', handlePaste);
    return () => editorDom.removeEventListener('paste', handlePaste);
  }, [editor, handleFileUpload]);

  // Handle drag and drop for files and images
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!editor) return;

    const editorDom = editor.view.dom;
    const editorContainer = editorDom.closest('.rich-text-editor');

    const handleDragEnter = (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(true);
    };

    const handleDragOver = (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'copy';
      }
    };

    const handleDragLeave = (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      // Only set isDragging to false if we're leaving the editor container
      const relatedTarget = event.relatedTarget as Node | null;
      if (editorContainer && !editorContainer.contains(relatedTarget)) {
        setIsDragging(false);
      }
    };

    const handleDrop = (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(false);

      const files = event.dataTransfer?.files;
      if (files && files.length > 0) {
        // Check if any file is an image
        const hasImages = Array.from(files).some(file => file.type.startsWith('image/'));
        handleFileUpload(files, hasImages);
      }
    };

    // Add listeners to the editor container to capture drag events properly
    if (editorContainer) {
      editorContainer.addEventListener('dragenter', handleDragEnter as EventListener);
      editorContainer.addEventListener('dragover', handleDragOver as EventListener);
      editorContainer.addEventListener('dragleave', handleDragLeave as EventListener);
      editorContainer.addEventListener('drop', handleDrop as EventListener);

      return () => {
        editorContainer.removeEventListener('dragenter', handleDragEnter as EventListener);
        editorContainer.removeEventListener('dragover', handleDragOver as EventListener);
        editorContainer.removeEventListener('dragleave', handleDragLeave as EventListener);
        editorContainer.removeEventListener('drop', handleDrop as EventListener);
      };
    }
  }, [editor, handleFileUpload]);

  if (!editor) return null;

  const iconSize = compact ? 16 : 18;

  return (
    <div className={`rich-text-editor border border-gray-200 rounded-xl bg-white overflow-hidden transition-all duration-200 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100 ${className} ${isFullscreen ? 'fixed inset-0 z-[9999] rounded-none h-screen' : ''}`}>
      {/* Bubble Menu */}
      {editor && (
        <BubbleMenu editor={editor} options={{ duration: 100 } as any}>
          <motion.div 
            drag
            dragMomentum={false}
            className="flex items-center gap-0.5 bg-gray-900 text-white rounded-lg shadow-xl p-1 border border-gray-700 overflow-hidden cursor-move"
          >
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleBold().run()}
              className={`p-1.5 rounded hover:bg-gray-800 transition-colors ${editor.isActive('bold') ? 'text-blue-400' : 'text-gray-300'}`}
              title="Bold"
            >
              <BiBold size={16} />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleItalic().run()}
              className={`p-1.5 rounded hover:bg-gray-800 transition-colors ${editor.isActive('italic') ? 'text-blue-400' : 'text-gray-300'}`}
              title="Italic"
            >
              <BiItalic size={16} />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              className={`p-1.5 rounded hover:bg-gray-800 transition-colors ${editor.isActive('underline') ? 'text-blue-400' : 'text-gray-300'}`}
              title="Underline"
            >
              <BiUnderline size={16} />
            </button>
            <div className="w-px h-4 bg-gray-700 mx-1" />
            <button
              type="button"
              onClick={() => setShowLinkInput(true)}
              className={`p-1.5 rounded hover:bg-gray-800 transition-colors ${editor.isActive('link') ? 'text-blue-400' : 'text-gray-300'}`}
              title="Link"
            >
              <BiLink size={16} />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleHighlight().run()}
              className={`p-1.5 rounded hover:bg-gray-800 transition-colors ${editor.isActive('highlight') ? 'text-yellow-400' : 'text-gray-300'}`}
              title="Highlight"
            >
              <BiHighlight size={16} />
            </button>
          </motion.div>
        </BubbleMenu>
      )}


      {/* Mode Switcher Tabs */}
      <div className="flex items-center border-b border-gray-100 bg-gray-50/50">
          <ModeTab 
            active={!isMarkdownMode}
            onClick={() => {
              setIsMarkdownMode(false);
              setShowSplitView(false);
            }}
            label="Write"
            icon={<BiCodeBlock size={14} />}
          />
          <ModeTab 
            active={isMarkdownMode}
            onClick={() => {
              setIsMarkdownMode(true);
              if (editor) {
                const md = (editor.storage as any).markdown.getMarkdown();
                setMarkdownValue(md);
              }
            }}
            label="Markdown"
            icon={<TbMarkdown size={16} />}
          />
        </div>

      {/* Toolbar */}
      {showToolbar && !isMarkdownMode && (
        <div className="flex items-center gap-0.5 px-2 py-1.5 bg-gray-50/80 border-b border-gray-100 flex-wrap">
          {/* Headings */}
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            active={editor.isActive('heading', { level: 1 })}
            title="Heading 1"
          >
            <TbH1 size={iconSize} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            active={editor.isActive('heading', { level: 2 })}
            title="Heading 2"
          >
            <TbH2 size={iconSize} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            active={editor.isActive('heading', { level: 3 })}
            title="Heading 3"
          >
            <TbH3 size={iconSize} />
          </ToolbarButton>

          <ToolbarDivider />

          {/* Text formatting */}
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            active={editor.isActive('bold')}
            title="Bold (Ctrl+B)"
          >
            <BiBold size={iconSize} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            active={editor.isActive('italic')}
            title="Italic (Ctrl+I)"
          >
            <BiItalic size={iconSize} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            active={editor.isActive('underline')}
            title="Underline (Ctrl+U)"
          >
            <BiUnderline size={iconSize} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleStrike().run()}
            active={editor.isActive('strike')}
            title="Strikethrough"
          >
            <BiStrikethrough size={iconSize} />
          </ToolbarButton>

          <ToolbarDivider />

          {/* Text Color */}
          <div className="relative">
            <ToolbarButton
              onClick={() => { setShowColorPicker(!showColorPicker); setShowHighlightPicker(false); setShowTableMenu(false); }}
              active={showColorPicker}
              title="Text Color"
            >
              <TbPalette size={iconSize} />
            </ToolbarButton>
            <AnimatePresence>
              {showColorPicker && (
                <motion.div
                  initial={{ opacity: 0, y: -5, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -5, scale: 0.95 }}
                  className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-xl p-2"
                >
                  <div className="grid grid-cols-6 gap-1 w-32">
                    {textColors.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => { editor.chain().focus().setColor(color).run(); setShowColorPicker(false); }}
                        className="w-4 h-4 rounded border border-gray-200 hover:scale-110 transition-transform"
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => { editor.chain().focus().unsetColor().run(); setShowColorPicker(false); }}
                    className="w-full mt-1.5 px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 rounded"
                  >
                    Remove color
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Highlight */}
          <div className="relative">
            <ToolbarButton
              onClick={() => { setShowHighlightPicker(!showHighlightPicker); setShowColorPicker(false); setShowTableMenu(false); }}
              active={editor.isActive('highlight') || showHighlightPicker}
              title="Highlight"
            >
              <BiHighlight size={iconSize} />
            </ToolbarButton>
            <AnimatePresence>
              {showHighlightPicker && (
                <motion.div
                  initial={{ opacity: 0, y: -5, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -5, scale: 0.95 }}
                  className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-xl p-2"
                >
                  <div className="grid grid-cols-6 gap-1 w-32">
                    {highlightColors.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => { editor.chain().focus().toggleHighlight({ color }).run(); setShowHighlightPicker(false); }}
                        className="w-4 h-4 rounded border border-gray-200 hover:scale-110 transition-transform"
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => { editor.chain().focus().unsetHighlight().run(); setShowHighlightPicker(false); }}
                    className="w-full mt-1.5 px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 rounded"
                  >
                    Remove highlight
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Subscript/Superscript */}
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleSubscript().run()}
            active={editor.isActive('subscript')}
            title="Subscript"
          >
            <TbSubscript size={iconSize} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleSuperscript().run()}
            active={editor.isActive('superscript')}
            title="Superscript"
          >
            <TbSuperscript size={iconSize} />
          </ToolbarButton>

          <ToolbarDivider />

          {/* Text Alignment */}
          <ToolbarButton
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            active={editor.isActive({ textAlign: 'left' })}
            title="Align Left"
          >
            <BiAlignLeft size={iconSize} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            active={editor.isActive({ textAlign: 'center' })}
            title="Align Center"
          >
            <BiAlignMiddle size={iconSize} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            active={editor.isActive({ textAlign: 'right' })}
            title="Align Right"
          >
            <BiAlignRight size={iconSize} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
            active={editor.isActive({ textAlign: 'justify' })}
            title="Justify"
          >
            <BiAlignJustify size={iconSize} />
          </ToolbarButton>

          <ToolbarDivider />

          {/* Lists */}
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            active={editor.isActive('bulletList')}
            title="Bullet List"
          >
            <BiListUl size={iconSize} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            active={editor.isActive('orderedList')}
            title="Numbered List"
          >
            <BiListOl size={iconSize} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleTaskList().run()}
            active={editor.isActive('taskList')}
            title="Task List"
          >
            <BiCheckSquare size={iconSize} />
          </ToolbarButton>

          {/* Blockquote */}
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            active={editor.isActive('blockquote')}
            title="Blockquote"
          >
            <TbBlockquote size={iconSize} />
          </ToolbarButton>

          {/* Horizontal Rule */}
          <ToolbarButton
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
            title="Horizontal Line"
          >
            <BiMinus size={iconSize} />
          </ToolbarButton>

          <ToolbarDivider />

          {/* Code */}
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleCode().run()}
            active={editor.isActive('code')}
            title="Inline Code"
          >
            <BiCode size={iconSize} />
          </ToolbarButton>
          <ToolbarButton
            onClick={toggleIntelligentCodeBlock}
            active={editor.isActive('codeBlock')}
            title="Code Block"
          >
            <BiCodeBlock size={iconSize} />
          </ToolbarButton>

          <ToolbarDivider />

          {/* Table */}
          <div className="relative">
            <ToolbarButton
              onClick={() => { setShowTableMenu(!showTableMenu); setShowColorPicker(false); setShowHighlightPicker(false); }}
              active={editor.isActive('table') || showTableMenu}
              title="Table"
            >
              <BiTable size={iconSize} />
            </ToolbarButton>
            <AnimatePresence>
              {showTableMenu && (
                <motion.div
                  initial={{ opacity: 0, y: -5, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -5, scale: 0.95 }}
                  className="absolute top-full right-0 mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-xl p-2 min-w-[160px]"
                >
                  <button
                    type="button"
                    onClick={() => { editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(); setShowTableMenu(false); }}
                    className="w-full px-2 py-1.5 text-xs text-left text-gray-700 hover:bg-gray-100 rounded flex items-center gap-2"
                  >
                    <BiTable size={14} /> Insert 3×3 Table
                  </button>
                  {editor.isActive('table') && (
                    <>
                      <div className="my-1 border-t border-gray-100" />
                      <button
                        type="button"
                        onClick={() => { editor.chain().focus().addColumnBefore().run(); setShowTableMenu(false); }}
                        className="w-full px-2 py-1 text-xs text-left text-gray-600 hover:bg-gray-100 rounded"
                      >
                        Add column before
                      </button>
                      <button
                        type="button"
                        onClick={() => { editor.chain().focus().addColumnAfter().run(); setShowTableMenu(false); }}
                        className="w-full px-2 py-1 text-xs text-left text-gray-600 hover:bg-gray-100 rounded"
                      >
                        Add column after
                      </button>
                      <button
                        type="button"
                        onClick={() => { editor.chain().focus().deleteColumn().run(); setShowTableMenu(false); }}
                        className="w-full px-2 py-1 text-xs text-left text-red-600 hover:bg-red-50 rounded"
                      >
                        Delete column
                      </button>
                      <div className="my-1 border-t border-gray-100" />
                      <button
                        type="button"
                        onClick={() => { editor.chain().focus().addRowBefore().run(); setShowTableMenu(false); }}
                        className="w-full px-2 py-1 text-xs text-left text-gray-600 hover:bg-gray-100 rounded"
                      >
                        Add row before
                      </button>
                      <button
                        type="button"
                        onClick={() => { editor.chain().focus().addRowAfter().run(); setShowTableMenu(false); }}
                        className="w-full px-2 py-1 text-xs text-left text-gray-600 hover:bg-gray-100 rounded"
                      >
                        Add row after
                      </button>
                      <button
                        type="button"
                        onClick={() => { editor.chain().focus().deleteRow().run(); setShowTableMenu(false); }}
                        className="w-full px-2 py-1 text-xs text-left text-red-600 hover:bg-red-50 rounded"
                      >
                        Delete row
                      </button>
                      <div className="my-1 border-t border-gray-100" />
                      <button
                        type="button"
                        onClick={() => { editor.chain().focus().deleteTable().run(); setShowTableMenu(false); }}
                        className="w-full px-2 py-1 text-xs text-left text-red-600 hover:bg-red-50 rounded"
                      >
                        Delete table
                      </button>
                    </>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Link */}
          <div className="relative">
            <ToolbarButton
              onClick={() => setShowLinkInput(!showLinkInput)}
              active={editor.isActive('link') || showLinkInput}
              title="Add Link"
            >
              <BiLink size={iconSize} />
            </ToolbarButton>
            <AnimatePresence>
              {showLinkInput && (
                <motion.div
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  className="absolute top-full right-0 mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-2 flex gap-2"
                >
                  <input
                    type="text"
                    value={linkUrl}
                    onChange={(e) => setLinkUrl(e.target.value)}
                    placeholder="https://..."
                    className="w-48 px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:border-blue-400"
                    onKeyDown={(e) => e.key === 'Enter' && setLink()}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={setLink}
                    className="px-2 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    Add
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Image */}
          <ToolbarButton
            onClick={() => imageInputRef.current?.click()}
            title="Insert Image"
            disabled={uploading}
          >
            <BiImage size={iconSize} />
          </ToolbarButton>

          {/* File attachment */}
          <ToolbarButton
            onClick={() => fileInputRef.current?.click()}
            title="Attach File"
            disabled={uploading}
          >
            <BiPaperclip size={iconSize} />
          </ToolbarButton>

          <ToolbarDivider />

          {/* Undo/Redo */}
          <ToolbarButton
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            title="Undo (Ctrl+Z)"
          >
            <BiUndo size={iconSize} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            title="Redo (Ctrl+Y)"
          >
            <BiRedo size={iconSize} />
          </ToolbarButton>

          <ToolbarDivider />

          <ToolbarButton
            onClick={() => setIsFullscreen(!isFullscreen)}
            active={isFullscreen}
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? <BiExitFullscreen size={iconSize} /> : <BiFullscreen size={iconSize} />}
          </ToolbarButton>


          {/* Upload indicator */}
          {uploading && (
            <div className="ml-2 flex items-center gap-1.5 text-xs text-gray-500">
              <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              Uploading...
            </div>
          )}
        </div>
      )}

      {/* Markdown Mode Toolbar */}
      {isMarkdownMode && (
        <div className="flex flex-wrap items-center gap-1 p-2 bg-white border-b border-gray-100 sticky top-0 z-20">
          <ToolbarButton
            onClick={() => setShowSplitView(!showSplitView)}
            active={showSplitView}
            title="Toggle Side-by-Side Preview"
            label="Preview"
          >
            <TbEye size={iconSize} />
          </ToolbarButton>

          <ToolbarDivider />

          <ToolbarButton
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            title="Undo (Ctrl+Z)"
          >
            <BiUndo size={iconSize} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            title="Redo (Ctrl+Y)"
          >
            <BiRedo size={iconSize} />
          </ToolbarButton>

          <ToolbarDivider />

          <ToolbarButton
            onClick={() => setIsFullscreen(!isFullscreen)}
            active={isFullscreen}
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? <BiExitFullscreen size={iconSize} /> : <BiFullscreen size={iconSize} />}
          </ToolbarButton>

          {/* Upload indicator */}
          {uploading && (
            <div className="ml-2 flex items-center gap-1.5 text-xs text-gray-500">
              <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              Uploading...
            </div>
          )}
        </div>
      )}

      {/* Editor Content with drag overlay */}
      <div className="relative">
        {isMarkdownMode ? (
          <div className={`flex flex-col ${showSplitView ? 'md:flex-row' : ''} border-b border-gray-100 min-h-[200px]`}>
            <textarea
              className={`flex-1 p-4 font-mono text-sm bg-gray-50/50 text-gray-800 focus:outline-none resize-none transition-all duration-200 ${showSplitView ? 'md:border-r border-gray-100' : ''}`}
              style={{ minHeight, maxHeight }}
              value={markdownValue}
              onChange={(e) => {
                const newVal = e.target.value;
                setMarkdownValue(newVal);
                
                // Update the editor silently without triggering onUpdate (and serializing it)
                (editor.commands as any).setContent(newVal, false);
                
                // Notify parent directly with raw/parsed content to avoid backslash escaping issues
                if (outputFormat === 'markdown') {
                  onChange?.(newVal, editor.getHTML());
                } else {
                  const parsedHTML = (editor.storage as any).markdown?.parser?.parse(newVal) || newVal;
                  onChange?.(parsedHTML, newVal);
                }
              }}
              placeholder="Type your markdown here..."
            />
            {showSplitView && (
              <div className="flex-1 p-4 bg-white overflow-y-auto" style={{ maxHeight }}>
                <RichTextRenderer content={markdownValue} />
              </div>
            )}
          </div>
        ) : (
          <EditorContent editor={editor} />
        )}

        {/* Drag and drop overlay */}
        <AnimatePresence>
          {isDragging && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="absolute inset-0 bg-blue-50/90 backdrop-blur-sm border-2 border-dashed border-blue-400 rounded-lg flex items-center justify-center z-10"
            >
              <div className="flex flex-col items-center gap-2 text-blue-600">
                <BiImage size={32} />
                <span className="text-sm font-medium">Drop files here to upload</span>
                <span className="text-xs text-blue-500">Images & documents supported</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => handleFileUpload(e.target.files)}
      />
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFileUpload(e.target.files, true)}
      />

      {/* Footer hint */}
      {!compact && (
        <div className="px-3 py-1.5 bg-gray-50/50 border-t border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3 text-[10px] text-gray-400">
            <span>Drag & drop or paste images</span>
          </div>
          <div className="text-[10px] text-gray-400 font-medium">
            {editor?.storage.characterCount.characters()} characters
          </div>
        </div>
      )}
    </div>
  );
});

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;
export { RichTextEditor };
